import { ChangeEventHandler, FocusEventHandler, ReactNode, SelectHTMLAttributes } from 'react';
import styled from '@emotion/styled';
import { colors, spacing } from '../../../utils/styleguide';
import { LabelContainer } from './TextInput/LabelContainer';
import { css } from '@emotion/react';

const SelectElementBorderWrapper = styled.div`
  display: flex;
  border: 1px solid ${colors.grey02};
  border-radius: 4px;
  overflow: hidden;
  padding: ${spacing[1]}px ${spacing[2]}px;
  width: 100%;
  height: 40px;
`;

const SelectElement = styled.select<Props>`
  display: flex;
  cursor: pointer;
  color: ${({ disabled }) => (disabled ? colors.grey : colors.black)};
  outline: none;
  flex: 1;
  border: none;
  padding: 0;
  background-color: ${({ disabled }) => (disabled ? colors.lightGray : colors.white)};

  ${({ hasFocus, disabled }) => {
    if (hasFocus && !disabled) {
      return css`
        border-color: ${colors.info};
      `;
    } else if (disabled) {
      return css`
        border-color: ${colors.whiteTransparentSemi};
      `;
    } else if (hasFocus || disabled) {
      return css`
        border-color: ${colors.grey02};
      `;
    }
  }}
`;

type Props = {
  id: string;
  children: ReactNode;
  label?: string;
  labelColor?: string;
  disabled?: boolean;
  value: SelectHTMLAttributes<HTMLSelectElement>['value'];
  onChange: ChangeEventHandler<HTMLSelectElement>;
  liftedLabel?: boolean;
  onFocus?: FocusEventHandler<HTMLSelectElement>;
  hasFocus?: boolean;
};

export default function Select({
  id,
  children,
  label,
  labelColor,
  disabled,
  value,
  onChange,
  onFocus,
  hasFocus,
}: Props) {
  return (
    <label
      css={css`
        position: relative;
        display: flex;
        width: 100%;
      `}
    >
      <LabelContainer
        labelColor={labelColor || colors.grey02}
        labelLifted={true}
        css={css`
          z-index: ${value ? 1 : -1};
        `}
      >
        {label}
      </LabelContainer>

      <SelectElementBorderWrapper>
        <SelectElement
          id={id}
          value={value}
          onChange={onChange}
          disabled={disabled}
          onFocus={onFocus}
          hasFocus={hasFocus}
        >
          {children}
        </SelectElement>
      </SelectElementBorderWrapper>
    </label>
  );
}
